import { useEffect, useState } from "react";

// Shared cart, persisted in localStorage. Holds course codes (strings) and
// administrative service items (objects), so a selection made on the Courses
// or Tuition page survives navigation to checkout.
const KEY = "egos-cart";
const listeners = new Set();

const read = () => {
  try {
    const v = JSON.parse(localStorage.getItem(KEY));
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
};

const emit = () => listeners.forEach((l) => l());

export const cartApi = {
  get: read,
  add(code) {
    const items = read();
    if (!items.includes(code)) {
      localStorage.setItem(KEY, JSON.stringify([...items, code]));
      emit();
    }
  },
  remove(code) {
    localStorage.setItem(KEY, JSON.stringify(read().filter((c) => c !== code)));
    emit();
  },
  addAdmin(item) {
    // One cart entry per service type — re-adding replaces the previous one.
    const items = read().filter((c) => typeof c === "string" || c.type !== item.type);
    localStorage.setItem(KEY, JSON.stringify([...items, item]));
    emit();
  },
  removeAdmin(type) {
    localStorage.setItem(KEY, JSON.stringify(read().filter((c) => typeof c === "string" || c.type !== type)));
    emit();
  },
  clear() {
    localStorage.removeItem(KEY);
    emit();
  },
  subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};

export function useCart() {
  const [items, setItems] = useState(read);
  useEffect(() => cartApi.subscribe(() => setItems(read())), []);
  return {
    items,
    courses: items.filter((c) => typeof c === "string"),
    admin: items.filter((c) => typeof c === "object"),
    has: (code) => items.includes(code),
    add: cartApi.add,
    remove: cartApi.remove,
    addAdmin: cartApi.addAdmin,
    removeAdmin: cartApi.removeAdmin,
    clear: cartApi.clear,
  };
}
